import {
  Drawer,
  FileUploader,
  Flex,
  Form,
  HintIcon,
  Space,
  SubmitButton,
} from '@/components';
import type { AttachmentsModel } from '@/features/attachment/_models/attachment';
import { useRelatedMaterials } from '@/features/project/_hooks/related-material';
import { useForm, useId } from '@/hooks';
import { useMessage } from '@/providers';
import { FALLBACK_TEXT } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import { RelatedMaterialModel } from '../../_models/related-material';
import { useProjectDetailDrawerState } from '../_components/ProjectDrawerStateProvider';
import { AttachedImageGroup } from './AttachedImageGroup';
import { useCreateAttachment, useUploadFile } from './attachment';
import {
  useCreateRelatedMaterial,
  useUpdateRelatedMaterial,
} from './related-material';

const RelatedMaterialFormSchema = RelatedMaterialModel.pick({
  attachments: true,
}).extend({
  projectId: z.string().uuid(),
});

type FormValues = z.infer<typeof RelatedMaterialFormSchema>;

export const OtherInformation: FC<{
  othersImages: z.infer<typeof AttachmentsModel> | undefined;
}> = ({ othersImages }) => {
  const { id } = useId();
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { mutateAsync: uploadFileAsync } = useUploadFile();
  const { openMessage } = useMessage();
  const { mutateAsync: createRelatedMaterialsAsync } =
    useCreateRelatedMaterial();
  const { mutateAsync: createAttachmentAsync } = useCreateAttachment();
  const { mutateAsync: updateRelatedMaterialsAsync } =
    useUpdateRelatedMaterial();

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  const relatedMaterials = useRelatedMaterials(id);
  const relatedMaterialImages = useMemo(
    () =>
      relatedMaterials.data?.flatMap(
        (relatedMaterial) => relatedMaterial.attachments,
      ),
    [relatedMaterials],
  );

  const editTargetRelatedMaterial = useMemo(
    () => relatedMaterials.data?.[0],
    [relatedMaterials],
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = useForm<FormValues>({
    defaultValues: { projectId: id },
    resolver: zodResolver(RelatedMaterialFormSchema),
  });

  useEffect(() => {
    setValue('attachments', othersImages ?? (null as any));
  }, [othersImages, setValue]);

  const { fields: attachmentFields, remove: removeAttachment } =
    useFieldArray<FormValues>({
      control,
      name: 'attachments',
    });

  const uploadAttachment = useCallback(
    async (file: File | null) => {
      try {
        if (!file) {
          return;
        }

        const attachment = await createAttachmentAsync({
          fileName: file.name,
          fileContentType: file.type,
        });
        await uploadFileAsync({
          presignedUrl: attachment.presignedUrl,
          file,
        });
        return attachment.id;
      } catch {
        await openMessage('error', t('error-message-with-file'));
      }
    },
    [createAttachmentAsync, uploadFileAsync, openMessage, t],
  );

  const onSubmitRelatedMaterial = async ({
    values,
    file,
  }: {
    values: z.infer<typeof RelatedMaterialFormSchema>;
    file: File | null;
  }) => {
    try {
      const uploadedAttachmentId = await uploadAttachment(file);

      const getAttachmentIds = (
        attachments: z.infer<typeof RelatedMaterialFormSchema>['attachments'],
        uploadedAttachmentId: string | undefined,
      ) => {
        if (uploadedAttachmentId) {
          return [uploadedAttachmentId];
        }
        return attachments.map((attachment) => attachment.id);
      };

      editTargetRelatedMaterial
        ? await updateRelatedMaterialsAsync({
            id: editTargetRelatedMaterial.id,
            ...values,
            attachmentIds: getAttachmentIds(
              values.attachments,
              uploadedAttachmentId,
            ),
          })
        : await createRelatedMaterialsAsync({
            ...values,
            attachmentIds: getAttachmentIds(
              values.attachments,
              uploadedAttachmentId,
            ),
          });

      await Promise.all([
        openMessage('success', t('message-update-completed')),
        handleClose(),
      ]);
    } catch {
      await openMessage('error', t('message-update-failed'));
    }
  };

  return (
    <>
      {relatedMaterialImages?.length ? (
        <Space size="small" direction="vertical">
          <AttachedImageGroup
            attachments={relatedMaterialImages}
            alt="related-material-image"
          />
        </Space>
      ) : (
        FALLBACK_TEXT
      )}
      <Drawer
        open={drawerState.open && drawerState.type === 'others'}
        onClose={() => {
          setDrawerState({ open: false });
        }}
        destroyOnHidden
      >
        <Form
          leavePageAlertCondition={null}
          layout="vertical"
          onFinish={handleSubmit(async (values: FormValues) => {
            await onSubmitRelatedMaterial({
              values,
              file: uploadedFile,
            });
          })}
        >
          <Form.Item
            label={
              <Flex gap={6}>
                {t('related-material')}
                <HintIcon hint={t('hint-upload-image-only')} />
              </Flex>
            }
          >
            <FileUploader
              files={uploadedFile ? [uploadedFile] : []}
              onChange={(acceptedFiles) => {
                setUploadedFile(acceptedFiles[0] ?? null);
              }}
              handleCancel={() => {
                setUploadedFile(null);
              }}
              storedFiles={{
                items: attachmentFields.map((field) => ({
                  name: field.fileName,
                  url: field.url,
                  fileContentType: field.fileContentType,
                })),
                onCancel: (index) => {
                  removeAttachment(index);
                },
              }}
              acceptableImage
              maxFiles={1}
              disabled={!!uploadedFile || attachmentFields.length > 0}
            />
          </Form.Item>
          <SubmitButton text={t('save')} loading={isSubmitting} />
        </Form>
      </Drawer>
    </>
  );
};
